import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PurchaseCard from '@componentsV2/PurchaseCard';
import InputField from '@vb/components/build/components/forms/inputs/InputField/InputField';
import CancelSubDialog from '@componentsV2/CancelSubDialog';
import {
  fetchPropertyOwner as apiFetchPropertyOwner,
  updatePropertyOwner as apiUpdatePropertyOwner,
  fetchConsents as apiFetchConsents,
  updateConsents as apiUpdateConsents,
  deletePropertyOwner as apiDeletePropertyOwner,
} from '../../../../../api/propertyowners';
import Form from '../../../../../components/forms/BaseForm';
import Typography from '../../../../../components/atomic/Typography';
import ErrorContainer from '../../../../../components/ErrorContainer';
import PageLayout from '../../../../../components/layouts/WithHeader';
import ContentLoader from '../../../../../components/loaders/ContentLoader';
import { getPropertyOwnerFullName } from '../../../../../utils/propertyowners';
import {
  MARKETINGEMAIL,
  PROPERTYVALUEESTIMATEUPDATES,
} from '../../../../../constants';
import Checkbox from '../../../../../components/atomic/Checkbox';
import {
  StyledProfileCard,
  StyledHeadline,
  StyledText,
  StyledProfilePage,
  StyledTermsBlock,
} from './styles';
import Link from '../../../../../components/atomic/Link';
import {
  boligmappaPlusCancel,
  getUserTransactions,
} from '../../../../../api/boligmappaPlus';
import {
  getActiveSubscriptions,
  getFormattedTransactionsData,
  getPaidSinglePayments,
} from '../../../../../utils/boligmappaPlus';
import useSearchQuery from '../../../../../hooks/useSearchQuery';
import useDesktop from '../../../../../hooks/useDesktop';
import useSnackbar from '../../../../../hooks/useSnackbar';
import useFetchEffect from '../../../../../hooks/useFetchEffect';
import useDashboardLinkPrefix from '../../../../../hooks/useDashboardLinkPrefix';
import matomo from '../../../../../utils/matomo';
import CircleQuestion from '../../../../../assets/icons/CircleQuestion';
import ProgressButton from '../../../../../components/ProgressButton';
import useActionDialog from '../../../../../hooks/useActionDialog';
import InfoTransparent from '../../../../../assets/icons/InfoTransparent';

export const defaultConsents = [
  {
    isGranted: false,
    type: MARKETINGEMAIL,
  },
];

export const getConsentByType = (consents, type) =>
  (consents || []).find((consent) => consent.type === type);

const Account = () => {
  const history = useHistory();
  const isDesktop = useDesktop();
  const { cancelled } = useSearchQuery();
  const dashboardLinkPrefix = useDashboardLinkPrefix();
  const { showSuccessMessage, showErrorMessage } = useSnackbar();
  const { showActionDialog } = useActionDialog();
  const [expanded, setExpanded] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [deleteProfileRequestError, setDeleteProfileRequestError] =
    useState(null);
  const [deleteProfileConfirmation, setDeleteProfileConfirmation] =
    useState(false);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [deleteRequestInProgress, setDeleteRequestInProgress] = useState(false);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [userTransactions, setUserTransactions] = useState({});
  const [isCancelDialog, setIsCancelDialog] = useState(false);
  const [activeCancelId, setActiveCancelId] = useState();

  const {
    fetching,
    fetchingError,
    data: propertyOwner,
    setData: setPropertyOwner,
  } = useFetchEffect({
    initialDataState: {},
    defaultDataState: {},
    initialFetchingState: true,
    apiFetchFunction: apiFetchPropertyOwner,
  });

  const { data: consents, setData: setConsents } = useFetchEffect({
    initialFetchingState: true,
    initialDataState: defaultConsents,
    defaultDataState: defaultConsents,
    apiFetchFunction: apiFetchConsents,
    onError: (error) => setRequestError(error),
  });

  const { email, mobile } = propertyOwner || {};

  const propertyOwnerName = getPropertyOwnerFullName(propertyOwner);

  const onCancel = (id) => {
    setActiveCancelId(id);
    setIsCancelDialog(true);
  };

  const setEmail = (value) =>
    setPropertyOwner({ ...propertyOwner, email: value });
  const setMobile = (value) =>
    setPropertyOwner({ ...propertyOwner, mobile: value });

  const fetchUserTransactions = async () => {
    const [error, response] = await getUserTransactions();
    if (!error && response) {
      setUserTransactions(response.responses);
    }
  };

  const fetchStoredUserTransactions = () => {
    if (userTransactions) {
      const transactionsData = [
        ...getActiveSubscriptions(userTransactions.subscriptions),
        ...getPaidSinglePayments(userTransactions.singlePayments),
      ];

      setTransactions(getFormattedTransactionsData(transactionsData));
      setLoading(false);
    }
  };

  const onSubscriptionCancel = async (subId) => {
    const [error, response] = await boligmappaPlusCancel(subId);
    if (!error && response) {
      setTransactions((prevState) =>
        prevState.filter((el) => el.orderNumber !== subId)
      );
      history.push(`/subscription-cancelled?redirect=${dashboardLinkPrefix}`);
      matomo.clickEvent({
        category: 'Boligmappa+',
        action: 'Subscription Cancelled Successfully via request',
      });
    } else {
      showErrorMessage('Noe gikk galt.');
    }
  };

  const onCancelConfirm = () => {
    onSubscriptionCancel(activeCancelId);
    setIsCancelDialog(false);
  };

  useEffect(() => {
    fetchUserTransactions();
    if (cancelled) {
      showSuccessMessage('Abonnementet er avsluttet!');
      history.replace({ search: '' });
    }
  }, []);

  useEffect(() => {
    fetchStoredUserTransactions();
  }, [userTransactions]);

  const onFormSubmit = async () => {
    setRequestError(null);
    setRequestInProgress(true);

    const [error, response] = await apiUpdatePropertyOwner({ email, mobile });

    if (error && !response) {
      setRequestError(error);
    } else {
      setPropertyOwner({
        ...propertyOwner,
        mobile,
        email,
      });
      showSuccessMessage();
    }
    setRequestInProgress(false);
  };

  const onConsentsSubmit = async () => {
    setRequestError(null);
    setRequestInProgress(true);

    const [errorConsent, consentResponse] = await apiUpdateConsents(consents);

    if (errorConsent && !consentResponse) {
      setRequestError(errorConsent);
    } else {
      showSuccessMessage();
    }
    setRequestInProgress(false);
  };

  const onDeleteProfileSubmit = async () => {
    showActionDialog({
      submitButtonText: 'Slett',
      message: 'Er du sikker på at du vil slette kontoen din?',
      onSubmit: async () => {
        setDeleteRequestInProgress(true);

        const [error, response] = await apiDeletePropertyOwner();

        if (error && !response) {
          setDeleteProfileRequestError(error);
        } else {
          showSuccessMessage(`
            Takk for at du valgte Boligmappa!
            Hvis du vil bruke Boligmappas tjenester igjen, finner du oss på www.boligmappa.no
          `);

          setTimeout(() => {
            history.push('/logout');
          }, 3000);
        }

        setDeleteRequestInProgress(false);
      },
    });
  };

  const onConsentsChange = (event, type) => {
    if (consents.find((consent) => consent.type === type)) {
      setConsents(
        consents.map((consent) =>
          type === consent.type
            ? { ...consent, isGranted: event.target.checked }
            : consent
        )
      );
    } else {
      setConsents([
        ...consents,
        {
          type,
          isGranted: event.target.checked,
        },
      ]);
    }
  };

  return (
    <PageLayout
      minFullHeight
      headerTitle={!isDesktop ? 'Dine personopplysninger' : ''}
    >
      {fetchingError ? (
        <ErrorContainer paddingTop={false} errorResponse={fetchingError} />
      ) : (
        <>
          {fetching ? (
            <>
              <ContentLoader
                style={{ margin: '27px 0' }}
                height={20}
                width={280}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <ContentLoader
                  style={{ margin: '27px 0' }}
                  height={260}
                  width={350}
                />
                <ContentLoader
                  style={{ margin: '27px 0' }}
                  height={260}
                  width={350}
                />
              </div>
              <ContentLoader
                style={{ margin: '27px 0' }}
                height={260}
                width={350}
              />
              <ContentLoader
                style={{ margin: '27px 0' }}
                height={260}
                width={350}
              />
            </>
          ) : (
            <StyledProfilePage>
              {isDesktop && (
                <Typography className="header" variant="h2">
                  Min profil
                </Typography>
              )}
              <div className="page-content">
                <div className="content">
                  <StyledProfileCard>
                    <StyledHeadline>Personopplysninger</StyledHeadline>
                    <StyledText>
                      Personopplysninger hentes gjennom folkeregisteret og kan
                      ikke endres av Boligmappa.
                    </StyledText>
                    <div className="data-row">
                      <label>Navn</label>
                      <p>{propertyOwnerName}</p>
                    </div>
                  </StyledProfileCard>
                  <StyledProfileCard>
                    <StyledHeadline>Kontaktinformasjon</StyledHeadline>
                    <Form
                      fetching={fetching || !(mobile?.length && email?.length)}
                      error={requestError}
                      onSubmit={onFormSubmit}
                      submitButtonText="Lagre"
                      className="form"
                      requestInProgress={propertyOwner && requestInProgress}
                      showCancelButton={false}
                      actionButtonsClassName="actionBtn"
                      leftActions={isDesktop}
                    >
                      <InputField
                        label="Epostadresse"
                        placeholder="nina.torset@gmail.com"
                        value={email || ''}
                        disabled={fetching || requestInProgress}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      <InputField
                        label="Mobilnummer"
                        placeholder="Skriv inn dit mobilnummer"
                        value={mobile || ''}
                        disabled={fetching || requestInProgress}
                        onChange={(e) => setMobile(e.target.value)}
                      />
                    </Form>
                  </StyledProfileCard>
                  <div className="purchases_cards">
                    {!loading &&
                      transactions.length > 0 &&
                      transactions.map((el) => (
                        <PurchaseCard
                          key={el.orderNumber}
                          classes="purchase_card"
                          type={el.type}
                          dashboardLinkPrefix={dashboardLinkPrefix}
                          date={el.createdDate}
                          orderNumber={el.orderNumber}
                          amount={el.amount}
                          nextBillingDate={el.nextBillingDate}
                          cancelled={el.cancelled}
                          onSubCancel={() => onCancel(el.orderNumber)}
                        />
                      ))}
                  </div>
                  <StyledProfileCard $fullWidth>
                    <StyledHeadline>Samtykker</StyledHeadline>
                    <StyledText>
                      Vi bruker e-postadressen til å sende deg velkomstmail,
                      viktige oppdateringer, varsler om dokumentopplastinger fra
                      dine håndverkere, informasjon om vesentlige endringer i
                      våre vilkår og eventuelt vedlikehold på nettsiden. I
                      tillegg kan du velge å motta:
                    </StyledText>
                    <Checkbox
                      style={{ paddingTop: 0, paddingBottom: 0 }}
                      onChange={(e) => onConsentsChange(e, MARKETINGEMAIL)}
                      className="checkbox"
                      classes={{ label: 'checkboxLabel' }}
                      labelTextClassName="labelTextClassName"
                      label="Jeg ønsker å motta e-post med tips, nyheter og arrangementer m.m."
                      checked={
                        (
                          getConsentByType(consents, MARKETINGEMAIL) || {
                            isGranted: false,
                          }
                        ).isGranted
                      }
                    />
                    <Checkbox
                      style={{ paddingTop: 0, paddingBottom: 0 }}
                      onChange={(e) =>
                        onConsentsChange(e, PROPERTYVALUEESTIMATEUPDATES)
                      }
                      className="checkbox"
                      classes={{ label: 'checkboxLabel' }}
                      labelTextClassName="labelTextClassName"
                      label="Jeg ønsker å motta månedlig e-post med oppdatert prisestimat for eiendommene mine."
                      checked={
                        (
                          getConsentByType(
                            consents,
                            PROPERTYVALUEESTIMATEUPDATES
                          ) || {
                            isGranted: false,
                          }
                        ).isGranted
                      }
                    />
                    <ProgressButton
                      className="submit-button"
                      variant="contained"
                      color="primary"
                      onClick={onConsentsSubmit}
                      requestInProgress={requestInProgress}
                    >
                      Lagre
                    </ProgressButton>
                  </StyledProfileCard>
                  <StyledProfileCard $fullWidth>
                    <StyledHeadline>Slett kontoen din</StyledHeadline>
                    <StyledText>
                      Du eier dataen tilknyttet din Boligmappe-bruker, selv om
                      du velger å slette den.
                    </StyledText>
                    <Accordion
                      expanded={expanded}
                      onChange={() => setExpanded(!expanded)}
                      classes={{ root: 'accordionRoot' }}
                    >
                      <AccordionSummary
                        classes={{
                          root: 'accordionSummary',
                          content: 'accordionContent',
                        }}
                        expandIcon={<ExpandMoreIcon />}
                      >
                        <CircleQuestion />
                        Hva skjer når du sletter Boligmappakontoen din?
                      </AccordionSummary>
                      <AccordionDetails className="accordionDetails">
                        <p>
                          Du eier dataen tilknyttet din Boligmappe-bruker, selv
                          om du velger å slette den.
                        </p>
                        <p>
                          Hva skjer når du sletter Boligmappa - kontoen din?
                        </p>
                        <ul>
                          <li>
                            Profilen slettes fra Boligmappa sine systemer.
                          </li>
                          <li>
                            E-postadresse og telefonnummer slettes umiddelbart.
                          </li>
                          <li>
                            Fødselsnummeret og personlige dokumenter som ligger
                            på ditt “Personlige område” slettes for alltid hvis
                            du ikke logger på igjen innen 3 måneder.
                          </li>
                          <li>
                            Sletting av kontoen vil ikke slette eiendommen fra
                            de system håndverkerne benytter for å loggføre sitt
                            arbeid mot eiendommer i Norge. Dette for å ta vare
                            på håndverkernes krav til dokumentasjon. Eiendommen
                            vil fortsatt være registrert i matrikkelen.
                            Matrikkelen administreres av Statens Kartverk.
                          </li>
                          <li>
                            Du kan miste tilgangen til andre tjenester du har
                            koblet Boligmappa til.
                          </li>
                        </ul>
                        <p>
                          Personopplysningene er lagret i sikkerhetskopier og
                          loggfiler hos oss slettes innen 1 år. Se
                          retningslinjene for sikkerhetskopiering.
                        </p>
                        <div className="alert-message">
                          <InfoTransparent /> Sletting er permanent, og kan ikke
                          tilbakestilles.
                        </div>
                      </AccordionDetails>
                    </Accordion>
                    <Form
                      fetching={
                        deleteRequestInProgress || !deleteProfileConfirmation
                      }
                      showCancelButton={false}
                      error={deleteProfileRequestError}
                      onSubmit={onDeleteProfileSubmit}
                      submitButtonText="Slett kontoen"
                      requestInProgress={deleteRequestInProgress}
                      formClassName="delete-form"
                      actionButtonsClassName="delete-form-actionBtn"
                    >
                      <Checkbox
                        required
                        disabled={deleteRequestInProgress}
                        onChange={() =>
                          setDeleteProfileConfirmation(
                            !deleteProfileConfirmation
                          )
                        }
                        checked={deleteProfileConfirmation}
                        className="checkbox"
                        classes={{ label: 'checkboxLabel' }}
                        labelTextClassName="labelTextClassName"
                        label="Ja, jeg forstår at jeg er ansvarlig for eventuelle konsekvenser av at jeg sletter kontoen min."
                        style={{ paddingTop: 0, paddingBottom: 0 }}
                      />
                    </Form>
                  </StyledProfileCard>
                  <StyledTermsBlock>
                    <Link
                      isExternal
                      openInNewTab
                      href="https://www.boligmappa.no/personvern"
                    >
                      <h2>Brukervilkår og personvern</h2>
                    </Link>
                  </StyledTermsBlock>
                </div>
                <aside className="help_aside">
                  <div className="help_box">
                    <h3>Trenger du hjelp?</h3>
                    <p>
                      På våre hjelpesider finner du spørsmål og svar på alt om
                      Boligmappa.
                    </p>
                    <a
                      href="https://support.boligmappa.no"
                      className="filled_button"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Besøk kundesenteret
                    </a>
                  </div>
                  <h4>Vilkår for kjøp</h4>
                  <ul className="link_list">
                    <li>
                      <a
                        href="https://www.boligmappa.no/vilkar-for-kjop"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Vilkår for kjøp (boligmappa.no)
                      </a>
                    </li>
                  </ul>
                </aside>
              </div>
              <CancelSubDialog
                isShown={isCancelDialog}
                onHide={() => setIsCancelDialog(false)}
                onSubCancel={onCancelConfirm}
              >
                <h3 className="dialog_title">Avslutt Boligmappa+?</h3>
                <p className="dialog_description">
                  Dette skjer når du avslutter ditt abonnement:
                </p>
                <ul className="dialog_list">
                  <li>
                    Din bruker mister tilgang til alt innhold fra Boligmappa+
                    når nåværende, betalte abonnementperiode utløper.
                  </li>
                  <li>
                    Du kan når som helst reaktivere abonnementet til den
                    gjeldende markedsprisen på reaktiveringstidspunktet.
                  </li>
                  <li>
                    Avsluttet abonnement vil ikke slette din bruker, dine
                    boliger etc. Kun tilgangen til innhold fra Boligmappa+.
                  </li>
                </ul>
              </CancelSubDialog>
            </StyledProfilePage>
          )}
        </>
      )}
    </PageLayout>
  );
};

export default Account;
